import { Calculator } from "lucide-react";
import Lottie from "lottie-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dimmer, Grid } from "semantic-ui-react";
import { usePutIngredientDataToQueue } from "../../../api/ingredients";
import calculatorLoading from "../../../assets/animations/CalculatorLoading.json";
import { CommonModal, InputText, TabView } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { IngredientViewTabs, TAB_KEYS } from "../../../config/constants";
import { IngredientDataType } from "../../../config/enums/IngredientType.enum";
import { MappingSourceType } from "../../../config/enums/MappingSourceType.enum";
import IngredientImpactTab from "./IngredientViewTabs/IngredientImpactTab";
import IngredientMappingTab from "./IngredientViewTabs/IngredientMappingTab";
import IngredientSummaryHistory from "./IngredientViewTabs/IngredientSummaryHistory";
import IngredientSummaryTab from "./IngredientViewTabs/IngredientSummaryTab";
import IngredientVolumeAndCost from "./IngredientViewTabs/IngredientVolumeAndCost";
import SupplierIngredientTab from "./IngredientViewTabs/SuppliersingredientsTab";

interface IngredientDetailsModalProps {
  ingredientDetails: any;
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  navigateIngredientEdit: (visible: boolean) => void;
}

const IngredientDetailsModal = ({
  ingredientDetails,
  visibleModal,
  setVisibleModal,
  navigateIngredientEdit
}: IngredientDetailsModalProps) => {
  const [selectedTab, setSelectedTab] = useState<Number>(0);
  const [calculateModalOpen, setCalculateModalOpen] = useState<boolean>(false);
  const navigation = useNavigate();

  const { mutate: putIngredientDataToQueue, isLoading: isLoadingPutDataToQueue } =
    usePutIngredientDataToQueue();

  const renderTabContent = () => {
    switch (selectedTab) {
      case TAB_KEYS.SUMMARY:
        return <IngredientSummaryTab ingredientDetails={ingredientDetails} />;
      case TAB_KEYS.ENVIRONMENTAL_FOOTPRINT:
        return <IngredientImpactTab ingredientDetails={ingredientDetails} />;
      case TAB_KEYS.SUPPLY_CHAIN_MAPPING:
        return <IngredientMappingTab ingredientDetails={ingredientDetails} />;
      case TAB_KEYS.HISTORY:
        return <IngredientSummaryHistory ingredientDetails={ingredientDetails} />;
      case TAB_KEYS.ANNUAL_VOLUME_AND_COST:
        const ingredientId = ingredientDetails?.ingredient_id;
        const supplierId = ingredientDetails?.supplier_id;
        const unitSize = parseFloat(ingredientDetails?.supplierIngredientDetails?.unit_size) || 1;
        const unitType = ingredientDetails?.supplierIngredientDetails?.unit_type || "kg";
        return (
          <IngredientVolumeAndCost
            ingredientId={ingredientId}
            supplierId={supplierId}
            unitSize={unitSize}
            unitType={unitType}
          />
        );
      case TAB_KEYS.SUPLIER_INGREDIENTS:
        return <SupplierIngredientTab ingredientDetails={ingredientDetails} />;
      default:
        return null;
    }
  };

  const ingredientId = ingredientDetails?.ingredient_id;
  const supplierId = ingredientDetails?.supplier_id;
  const currentDataType = ingredientDetails?.supplierIngredientDetails?.data_type;

  const calculateHandleApprove = () => {
    putIngredientDataToQueue(
      { ingredientId, supplierId },
      {
        onSuccess: () => {
          setCalculateModalOpen(false);
          setSelectedTab(TAB_KEYS.ENVIRONMENTAL_FOOTPRINT);
        },
        onError: () => {
          setCalculateModalOpen(false);
        }
      }
    );
  };

  const calculationTile = "Are you sure you have entered all the data for ingredient calculation?";
  const ingredientButtonTitle =
    currentDataType === IngredientDataType.USER ? "Ingredient Mapping" : "Calculate";

  const enableIngredientMapping = useMemo(
    () =>
      (currentDataType === IngredientDataType.USER ||
        currentDataType === IngredientDataType.GENERIC) &&
      ingredientId &&
      supplierId,
    [currentDataType]
  );

  const navigateIngredientMapping = () =>
    navigation(
      `/mapping/${MappingSourceType.ingredient}?sourceId=${ingredientId}&ownerId=${supplierId}&ingredientName=${ingredientDetails?.ingredient_name}`
    );

  const handleIngredientButtonClick = () => {
    if (currentDataType === IngredientDataType.USER) {
      navigateIngredientMapping();
    } else {
      setCalculateModalOpen(true);
    }
  };

  if (isLoadingPutDataToQueue) {
    return (
      <Dimmer active>
        <div className="loading-calculation">
          <Lottie animationData={calculatorLoading} loop={true} />
        </div>
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => setVisibleModal(false)}
        centered={true}
        title="Ingredient Details"
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16} className="userBankDetailsMain">
            <CommonSplitContainer
              topChild={
                <>
                  <Grid>
                    <Grid.Column computer={8} tablet={8} mobile={8}>
                      <InputText
                        labelName={"Ingredient Name*"}
                        placeholder="Ingredient Name"
                        name="ingredient_name"
                        defaultValues={ingredientDetails?.ingredient_name}
                        disabled={true}
                      />
                    </Grid.Column>
                    <Grid.Column computer={8} tablet={8} mobile={8}>
                      <InputText
                        labelName={"Supplier Name*"}
                        placeholder="Supplier Name"
                        name="supplier_name"
                        defaultValues={ingredientDetails?.supplier_name}
                        disabled={true}
                      />
                    </Grid.Column>
                  </Grid>
                  <TabView
                    tabData={IngredientViewTabs}
                    computer={2}
                    tablet={16}
                    mobile={16}
                    defaultSelect={selectedTab}
                    customMainTab="ml-0"
                    disable={false}
                    onChange={(index: number) => setSelectedTab(index)}
                  />
                  {renderTabContent()}
                  <ConfirmModal
                    viewModal={calculateModalOpen}
                    closeModal={() => {
                      setCalculateModalOpen(false);
                    }}
                    cancel={() => setCalculateModalOpen(false)}
                    approve={calculateHandleApprove}
                    title={"Calculation Ingredient Data"}
                    subTitle={calculationTile}
                    variant="default"
                    confirmText="Calculate"
                    icon={<Calculator className="tw-h-5 tw-w-5" />}
                  />
                </>
              }
              bottomChild={
                <MainBottomMultipleButtonView
                  leftButtons={[
                    {
                      title: "Cancel",
                      onClick: () => setVisibleModal(false),
                      buttonOutLine: true
                    }
                  ]}
                  rightButtons={[
                    {
                      contentView: enableIngredientMapping ? 8 : 16,
                      title: "Edit",
                      onClick: () => navigateIngredientEdit(true),
                      type: "submit",
                      disabled: false,
                      buttonView: true
                    },
                    {
                      contentView: enableIngredientMapping ? 8 : 16,
                      title: ingredientButtonTitle,
                      onClick: () => handleIngredientButtonClick(),
                      type: "submit",
                      buttonView: enableIngredientMapping,
                      disabled: false
                    }
                  ]}
                />
              }
            />
          </Grid.Column>
        </Grid>
      </CommonModal>
    </>
  );
};

export default IngredientDetailsModal;
